import { FeatureName } from '@gripp/shared-logic';
import { UIMatch } from 'react-router-dom';

/**
 * The `handle` bag routes attach to gate themselves.
 *
 * React Router types `handle` as `unknown`, so every reader was casting with
 * `as any`. This names the shape once instead, which keeps the gate keys
 * spelled consistently across `routes.tsx` files and lets a typo in one be a
 * compile error rather than a route that silently gates on nothing.
 *
 * Both keys are optional and independent: a route can require a permission, a
 * feature flag, both, or neither. `ProtectedRoute` reads the innermost value
 * of each, so a child route can tighten a parent's gate.
 */
export type RouteHandle = {
  /** A `Permissions` value, checked with `account.hasPermission`. */
  requiredPermission?: string;
  requiredFeature?: FeatureName;
};

/** Reads a gate key off a matched route without an `as any` at every call. */
export const getRouteHandleValue = <K extends keyof RouteHandle>(
  matches: UIMatch[],
  key: K,
): RouteHandle[K] =>
  matches
    .map((match) => (match.handle as RouteHandle | undefined)?.[key])
    .filter((value): value is NonNullable<RouteHandle[K]> => Boolean(value))
    .pop();
